import qz from 'qz-tray';
import axios from 'axios';

/*
 * QZ Tray integration — silent label printing from the browser to a local printer.
 *
 * QZ Tray is a free desktop agent (system tray) that exposes the machine's OS printers
 * over a localhost WebSocket. The browser opens that socket and sends a base64 PDF straight
 * to a chosen printer — no "open PDF in a new tab and hit Ctrl+P" step.
 *
 * Design rules (see the QZ Tray rollout plan):
 *   - Printers are bound to the PHYSICAL machine, so the chosen printer names live in
 *     localStorage (per station), NOT on the server. Two slots:
 *        qz_printer_zebra  -> thermal/Zebra printer for FNSKU / product (2"x1") labels
 *        qz_printer_label  -> 4x6 / document printer for box / carrier / pallet labels
 *   - Every print path keeps its CURRENT behavior (open the PDF / download) as an automatic
 *     fallback: if the agent isn't running, no printer is chosen, or the print is blocked,
 *     we silently fall back so printing never breaks.
 *   - Path A (free / unsigned) ships first: the agent shows an "Allow / Remember" prompt once
 *     per agent session per station. Path B (paid cert -> true silent) is a drop-in toggle:
 *     set REACT_APP_QZ_CERT_URL + REACT_APP_QZ_SIGN_URL and configureSigning() wires it up.
 */

export const PRINTER_KEYS = { zebra: 'qz_printer_zebra', label: 'qz_printer_label' };

// Fired on the window after a printer slot changes so same-tab listeners (the settings card,
// the nudge banner) can re-read config — the native `storage` event only fires in OTHER tabs.
export const PRINTERS_CHANGED_EVENT = 'qz-printers-changed';

export const getSavedPrinter = (target) => {
  try {
    return localStorage.getItem(PRINTER_KEYS[target]) || '';
  } catch (e) {
    return '';
  }
};

export const savePrinter = (target, name) => {
  try {
    if (name) localStorage.setItem(PRINTER_KEYS[target], name);
    else localStorage.removeItem(PRINTER_KEYS[target]);
  } catch (e) {
    /* localStorage unavailable (private mode / quota) — ignore, printing still falls back */
  }
  try {
    window.dispatchEvent(new Event(PRINTERS_CHANGED_EVENT));
  } catch (e) {
    /* no-op */
  }
};

// "Configured" = both slots chosen. The nudge UX keys off this; it is deliberately NOT tied to a
// live socket, so the banner doesn't flicker whenever the agent happens to be off.
export const isPrinterConfigured = () =>
  Boolean(getSavedPrinter('zebra')) && Boolean(getSavedPrinter('label'));

/* ----------------------------- Path B: message signing (silent printing) ---------------------- */
// When the backend QZ endpoints are configured (a private key is present server-side), this wires
// QZ's certificate + signature promises so every request is cryptographically signed -> the agent
// prints SILENTLY (no Allow/Block prompt). Our certificate is issued by QZ Industries, whose CA
// every QZ Tray trusts natively — no per-station trust setup needed.
//
// Degrades safely: if the backend has no key, /qz/digital-certificate 404s -> the certificate promise
// rejects -> QZ treats the request as unsigned and shows the Allow/Remember prompt (free tier). The
// requests go through the app's axios instance, which carries the auth token, so the endpoints stay
// behind auth (only logged-in users can fetch a signature).
let signingConfigured = false;
export const configureSigning = ({ certUrl, signUrl, algorithm = 'SHA512' } = {}) => {
  if (signingConfigured || !certUrl || !signUrl) return;
  qz.security.setCertificatePromise((resolve, reject) =>
    axios.get(certUrl).then((r) => resolve(r.data)).catch(reject));
  qz.security.setSignatureAlgorithm(algorithm); // 2.1+ (2.0 used SHA1)
  qz.security.setSignaturePromise((toSign) => (resolve, reject) =>
    axios.get(signUrl, { params: { request: toSign } }).then((r) => resolve(r.data)).catch(reject));
  signingConfigured = true;
};

/* ----------------------------- connection ----------------------------------------------------- */
export const isConnected = () => {
  try {
    return qz.websocket.isActive();
  } catch (e) {
    return false;
  }
};

// Connect once; safe to call repeatedly (isActive guard avoids "connection already active").
// Low retries by default so a missing agent fails fast into the fallback instead of hanging.
export const connect = async ({ retries = 2, delay = 1 } = {}) => {
  if (isConnected()) return true;
  // Point signing at the backend QZ endpoints (override per-env if ever hosted elsewhere). Wiring is
  // harmless when the server has no key — the cert fetch 404s and QZ just falls back to prompting.
  const apiBase = process.env.OPULENT_NODE_API_URL || '';
  configureSigning({
    certUrl: process.env.REACT_APP_QZ_CERT_URL || (apiBase && `${apiBase}/qz/digital-certificate`),
    signUrl: process.env.REACT_APP_QZ_SIGN_URL || (apiBase && `${apiBase}/qz/sign`),
  });
  await qz.websocket.connect({ retries, delay });
  return true;
};

export const disconnect = async () => {
  try {
    if (isConnected()) await qz.websocket.disconnect();
  } catch (e) {
    /* no-op */
  }
};

// Reconnect WITHOUT any risk of a popup. The websocket dies with the tab (reload, sleep, QZ Tray
// restart), so status badges go "not connected" until something reconnects. With the signed cert
// live on the backend, connect() is silent — but if signing is NOT configured, connecting would
// pop QZ's Allow/Block dialog uninvited. So probe the cert endpoint first and only connect when
// it answers (404 = signing off -> stay out and let the user connect explicitly).
export const connectIfSilent = async () => {
  if (isConnected()) return true;
  const apiBase = process.env.OPULENT_NODE_API_URL || '';
  const certUrl = process.env.REACT_APP_QZ_CERT_URL || (apiBase && `${apiBase}/qz/digital-certificate`);
  if (!certUrl) return false;
  try {
    await axios.get(certUrl);
    await connect({ retries: 1, delay: 1 });
    return true;
  } catch (e) {
    return false; // no signing / agent not running — caller keeps the manual Connect flow
  }
};

// List installed printers (used by the settings dropdowns). Connects first; throws if the agent
// isn't running so the caller can show "QZ Tray not detected".
export const listPrinters = async () => {
  await connect({ retries: 3, delay: 1 });
  const found = await qz.printers.find();
  return (Array.isArray(found) ? found : [found]).filter(Boolean);
};

/* ----------------------------- printing ------------------------------------------------------- */
// Sensible default media per slot. Callers can override size/density/orientation per label type.
const DEFAULT_SIZE = {
  zebra: { width: 2.25, height: 1.25 }, // FNSKU / product thermal label
  label: { width: 4, height: 6 }, // box / carrier / pallet 4x6
};

/**
 * Print a base64-encoded PDF to a target slot's printer.
 * On ANY failure (no agent, no printer chosen, blocked, print error) runs `opts.fallback`
 * (today's behavior — e.g. open the PDF in a new tab) and resolves false instead of throwing.
 *
 * @param {'zebra'|'label'} target
 * @param {string} pdfBase64  raw base64 (no data: prefix)
 * @param {object} [opts]  { printer, size, units, orientation, density, colorType,
 *                           scaleContent, rasterize, copies, fallback }
 * @returns {Promise<boolean>} true if it printed via QZ, false if it fell back
 */
export const printPdfBase64 = async (target, pdfBase64, opts = {}) => {
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => {};
  const printerName = opts.printer || getSavedPrinter(target);
  if (!printerName || !pdfBase64) {
    fallback();
    return false;
  }
  try {
    if (!isConnected()) await connect({ retries: 1, delay: 1 });
    const config = qz.configs.create(printerName, {
      size: opts.size || DEFAULT_SIZE[target] || DEFAULT_SIZE.label,
      units: opts.units || 'in',
      orientation: opts.orientation || 'portrait',
      density: opts.density || 203,
      colorType: opts.colorType || 'blackwhite',
      scaleContent: opts.scaleContent !== false,
      rasterize: opts.rasterize === true,
      copies: opts.copies || 1,
    });
    await qz.print(config, [{ type: 'pixel', format: 'pdf', flavor: 'base64', data: pdfBase64 }]);
    return true;
  } catch (e) {
    fallback();
    return false;
  }
};

// Convenience for the jsPDF generators: hand it the jsPDF `doc` and it extracts base64 itself.
// jsPDF 1.x: output('datauristring') -> "data:application/pdf;base64,XXXX" -> take the base64 tail.
export const printJsPdf = (target, doc, opts = {}) => {
  let base64;
  try {
    base64 = doc.output('datauristring').split(',')[1];
  } catch (e) {
    if (typeof opts.fallback === 'function') opts.fallback();
    return Promise.resolve(false);
  }
  return printPdfBase64(target, base64, opts);
};

// Convenience for server/S3 PDFs (FBA in/out, carrier, pallet, BoL): fetch -> base64 -> print.
// Fallback defaults to opening the URL in a new tab (today's behavior for those paths).
export const printPdfUrl = async (target, url, opts = {}) => {
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => window.open(url, '_blank');
  try {
    const res = await fetch(url, opts.fetchInit);
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const blob = await res.blob();
    const base64 = await blobToBase64(blob);
    return printPdfBase64(target, base64, { ...opts, fallback });
  } catch (e) {
    fallback();
    return false;
  }
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Convenience for already-fetched PDFs held as a Blob (e.g. FBA in/out labels): Blob -> base64 -> print.
// Fallback (download / open) is supplied by the caller and runs on any failure.
export const printPdfBlob = async (target, blob, opts = {}) => {
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => {};
  try {
    const base64 = await blobToBase64(blob);
    return printPdfBase64(target, base64, { ...opts, fallback });
  } catch (e) {
    fallback();
    return false;
  }
};

export default qz;
